<template>
  <v-card class="mx-auto">
    <v-card-title class="text-h6 font-weight-regular">
      {{ scopeOnly ? "Définir le périmètre" : "Vérifier et approuver le plan" }}
    </v-card-title>
    <v-card-text>
      <v-alert v-if="scopeOnly" type="info" outlined dense>
        Cette étape lit les flux WordPress sans modifier <code>adn_post</code>.
        Elle enregistre uniquement le plan et ses checkpoints techniques.
      </v-alert>
      <v-alert v-else type="info" outlined dense>
        Contrôlez les volumes, les mois et les sites concernés avant
        d’approuver. Aucun contenu n’est ingéré pendant cette étape.
      </v-alert>

      <template v-if="!reviewOnly">
        <v-row class="mt-2">
          <v-col cols="5">Sites à auditer</v-col>
          <v-col cols="7">
            <v-autocomplete
              v-model="selectedDomains"
              :items="availableSites"
              item-text="domain"
              item-value="domain"
              label="Sélectionner un ou plusieurs sites"
              multiple
              deletable-chips
              outlined
              :disabled="planBuilding"
              :menu-props="{ maxHeight: '400' }"
            ></v-autocomplete>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="5">Période à auditer</v-col>
          <v-col cols="7">
            <v-menu
              v-model="menuDate"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dates"
                  label="Date de début et date de fin"
                  readonly
                  :disabled="planBuilding"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dates"
                range
                :max="yesterday"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

        <v-btn
          color="primary"
          :disabled="!canGeneratePlan || planBuilding"
          :loading="generating || planBuilding"
          @click="generatePlan"
        >
          Générer le plan
        </v-btn>

        <v-alert
          v-if="currentPlan && currentPlan.status === 'building'"
          class="mt-4 mb-0"
          type="info"
          outlined
        >
          Plan #{{ currentPlan.id }} en cours de construction. La vérification
          s’ouvrira automatiquement lorsque tous les flux auront été lus.
        </v-alert>
        <v-alert
          v-else-if="currentPlan && currentPlan.status === 'invalid'"
          class="mt-4 mb-0"
          type="error"
          outlined
        >
          Le plan #{{ currentPlan.id }} est invalide. La vérification ne peut
          pas continuer car au moins un flux n’a pas été lu intégralement.
          <ul>
            <li
              v-for="(error, domain) in currentPlan.errors_json"
              :key="domain"
            >
              {{ domain }} : {{ error }}
            </li>
          </ul>
        </v-alert>
      </template>

      <template v-if="!scopeOnly">
        <v-divider class="my-4"></v-divider>
        <v-row v-if="showRecentPlans" class="mt-2">
          <v-col cols="5">Plans récents</v-col>
          <v-col cols="7">
            <v-select
              v-model="selectedPlanId"
              :items="recentPlanOptions"
              item-text="label"
              item-value="id"
              label="Consulter un plan déjà généré"
              outlined
              :loading="loadingRecent"
              @change="loadPlan"
            ></v-select>
          </v-col>
        </v-row>

        <div v-if="currentPlan">
          <v-alert
            v-if="currentPlan.status === 'building'"
            type="info"
            outlined
          >
            Le plan #{{ currentPlan.id }} est en cours de construction (lecture
            des flux)…
          </v-alert>

          <v-alert
            v-else-if="currentPlan.status === 'invalid'"
            type="error"
            outlined
          >
            Le plan #{{ currentPlan.id }} est invalide : au moins un domaine n’a
            pas pu être lu intégralement. Il ne peut pas être approuvé.
            <ul>
              <li
                v-for="(error, domain) in currentPlan.errors_json"
                :key="domain"
              >
                {{ domain }} : {{ error }}
              </li>
            </ul>
          </v-alert>

          <div v-else-if="currentPlan.plan_json">
            <p>
              Plan #{{ currentPlan.id }} — statut :
              <b>{{ currentPlan.status }}</b> —
              {{ currentPlan.missing_ids }} IDs manquants sur
              {{ currentPlan.batch_count }} lot(s) site/mois.
            </p>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Domaine</th>
                  <th>IDs manquants</th>
                  <th>Dont IA</th>
                  <th>Mois concernés</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="summary in currentPlan.plan_json.domain_summaries"
                  :key="summary.domain"
                >
                  <td>{{ summary.domain }}</td>
                  <td>{{ summary.missing_ids }}</td>
                  <td>{{ summary.missing_ia_ids }}</td>
                  <td>{{ summary.affected_months.join(", ") }}</td>
                </tr>
              </tbody>
            </v-simple-table>

            <v-btn class="mt-3 mr-2" @click="downloadPlanJson">
              Télécharger le JSON
            </v-btn>
            <v-btn
              v-if="currentPlan.status === 'ready'"
              class="mt-3"
              color="primary"
              :loading="approving"
              @click="approvePlan"
            >
              Approuver ce plan
            </v-btn>
            <template v-else-if="currentPlan.status === 'approved'">
              <v-chip class="mt-3 mr-2" color="success" dark>
                Approuvé par {{ currentPlan.approved_by }}
              </v-chip>
              <v-btn
                class="mt-3"
                color="primary"
                @click="$emit('plan-approved', currentPlan)"
              >
                Continuer avec ce plan
              </v-btn>
            </template>
          </div>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from "@axios";
import store from "@/store";

const POLL_INTERVAL_MS = 4000;

export default {
  name: "AdnAudienceBackfillPlan",
  props: {
    siteList: {
      type: Array,
      default: () => [],
    },
    scopeOnly: {
      type: Boolean,
      default: false,
    },
    reviewOnly: {
      type: Boolean,
      default: false,
    },
    initialPlanId: {
      type: Number,
      default: null,
    },
    showRecentPlans: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    return {
      selectedDomains: [],
      dates: [],
      menuDate: false,
      yesterday: yesterday.toISOString().slice(0, 10),
      generating: false,
      approving: false,
      recentPlans: [],
      loadingRecent: false,
      selectedPlanId: null,
      currentPlan: null,
      pollTimer: null,
    };
  },
  computed: {
    availableSites() {
      return this.siteList.filter((site) => site.ga_view_id);
    },
    normalizedDates() {
      return [...this.dates].sort();
    },
    canGeneratePlan() {
      return (
        this.selectedDomains.length > 0 && this.normalizedDates.length === 2
      );
    },
    planBuilding() {
      return this.currentPlan && this.currentPlan.status === "building";
    },
    recentPlanOptions() {
      return this.recentPlans.map((plan) => ({
        id: plan.id,
        label: `#${plan.id} — ${plan.status} — ${plan.domains_json.join(", ")}`,
      }));
    },
  },
  async created() {
    if (!this.scopeOnly) {
      if (this.showRecentPlans) {
        await this.fetchRecentPlans();
      }
      if (this.initialPlanId) {
        this.selectedPlanId = this.initialPlanId;
        await this.loadPlan();
      }
    }
  },
  watch: {
    async initialPlanId(value) {
      if (!value || this.scopeOnly) {
        return;
      }
      this.selectedPlanId = value;
      await this.loadPlan();
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async fetchRecentPlans() {
      this.loadingRecent = true;
      try {
        const response = await axios.get("/adn-audience-backfill/plans", {
          params: { limit: 20 },
        });
        this.recentPlans = response.data;
      } finally {
        this.loadingRecent = false;
      }
    },
    async generatePlan() {
      this.generating = true;
      try {
        const [dateFrom, dateTo] = this.normalizedDates;
        const response = await axios.post("/adn-audience-backfill/plans", {
          domains: [...new Set(this.selectedDomains)],
          date_from: dateFrom,
          date_to: dateTo,
        });
        this.currentPlan = response.data;
        this.selectedPlanId = response.data.id;
        this.$emit("plan-created", response.data);
        if (response.data.status === "building") {
          this.startPolling();
        } else if (response.data.status === "ready") {
          this.$emit("plan-ready", response.data);
        }
        if (!this.scopeOnly && this.showRecentPlans) {
          await this.fetchRecentPlans();
        }
      } catch (error) {
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `Erreur(s): ${JSON.stringify(error.response?.data, null, 2)}`,
          color: "warning",
          timeout: 5000,
        });
      } finally {
        this.generating = false;
      }
    },
    async loadPlan() {
      this.stopPolling();
      if (!this.selectedPlanId) {
        this.currentPlan = null;
        return;
      }
      const response = await axios.get(
        `/adn-audience-backfill/plans/${this.selectedPlanId}`
      );
      this.currentPlan = response.data;
      if (this.currentPlan.status === "building") {
        this.startPolling();
      }
    },
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(async () => {
        if (!this.selectedPlanId) {
          return;
        }
        const response = await axios.get(
          `/adn-audience-backfill/plans/${this.selectedPlanId}`
        );
        this.currentPlan = response.data;
        if (this.currentPlan.status !== "building") {
          this.stopPolling();
          if (this.currentPlan.status === "ready") {
            this.$emit("plan-ready", this.currentPlan);
          }
          if (!this.scopeOnly && this.showRecentPlans) {
            await this.fetchRecentPlans();
          }
        }
      }, POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async approvePlan() {
      this.approving = true;
      try {
        const response = await axios.post(
          `/adn-audience-backfill/plans/${this.currentPlan.id}/approve`
        );
        this.currentPlan = response.data;
        this.$emit("plan-approved", this.currentPlan);
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `Plan #${this.currentPlan.id} approuvé.`,
          color: "success",
          timeout: 5000,
        });
      } catch (error) {
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `Erreur(s): ${JSON.stringify(error.response?.data, null, 2)}`,
          color: "warning",
          timeout: 5000,
        });
      } finally {
        this.approving = false;
      }
    },
    downloadPlanJson() {
      const blob = new Blob(
        [JSON.stringify(this.currentPlan.plan_json, null, 2)],
        { type: "application/json" }
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `adn_audience_backfill_plan_${this.currentPlan.id}.json`;
      link.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>
